import { masIdsEqual } from './mas-ids';
import { sortWaitListByAddedAt } from './wait-list-sort';

/** Wait-list row shape required for FIFO promotion (server `WaitList` / client equivalents). */
export interface WaitListPromotableRow {
	accountId: string;
	addedAt?: string;
	name: string;
	notifications?: unknown;
}

/** Attendance row appended when the oldest wait-list row is promoted into the class. */
export interface PromotionResultAttendanceRow {
	attended: false;
	id: string;
	name: string;
	notifications?: unknown;
	promotedFromWaitList: true;
	reserved: true;
	scheduleId: string;
}

export type PromoteNextFromWaitListResult<TAtt extends object, TWait extends WaitListPromotableRow> = {
	attendance: Array<TAtt | PromotionResultAttendanceRow>;
	waitList: TWait[];
	promoted?: TWait;
	changed: boolean;
};

/**
 * Moves the FIFO wait-list row into attendance when below capacity (single step).
 */
export function promoteNextFromWaitList<TAtt extends object, TWait extends WaitListPromotableRow>(
	scheduleId: string,
	attendance: readonly TAtt[],
	waitList: readonly TWait[],
	reserveLimit: number,
): PromoteNextFromWaitListResult<TAtt, TWait> {
	if (attendance.length >= reserveLimit || waitList.length === 0) {
		return { attendance: [...attendance], waitList: [...waitList], changed: false };
	}

	const sorted = sortWaitListByAddedAt([...waitList]);
	const next = sorted[0];
	const wl = waitList.filter(w => !(masIdsEqual(w.accountId, next.accountId) && w.addedAt === next.addedAt));
	const att: Array<TAtt | PromotionResultAttendanceRow> = [
		...attendance,
		{
			attended: false,
			id: next.accountId,
			name: next.name,
			notifications: next.notifications ?? undefined,
			promotedFromWaitList: true,
			reserved: true,
			scheduleId,
		},
	];

	return { attendance: att, waitList: wl, promoted: next, changed: true };
}

/**
 * While attendance is below capacity and the wait list has entries, move the
 * FIFO wait-list row into attendance (matches My `MASScheduleModel.promoteFromWaitList`).
 */
export function promoteWaitListIntoAttendance<TAtt extends object, TWait extends WaitListPromotableRow>(
	scheduleId: string,
	attendance: readonly TAtt[],
	waitList: readonly TWait[],
	reserveLimit: number,
): { attendance: Array<TAtt | PromotionResultAttendanceRow>; waitList: TWait[] } {
	let att: Array<TAtt | PromotionResultAttendanceRow> = [...attendance];
	let wl: TWait[] = [...waitList];

	while (att.length < reserveLimit && wl.length > 0) {
		const step = promoteNextFromWaitList(scheduleId, att, wl, reserveLimit);
		att = step.attendance;
		wl = step.waitList;
	}

	return { attendance: att, waitList: wl };
}
